import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, tap, withLatestFrom } from 'rxjs';
import { AccountClientService } from '@mode/shared/data-access-webapp';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as ApiKeyActions from './api-key.actions';
import * as ApiKeySelectors from './api-key.selectors';
import { OrgTypes } from '@mode/shared/contract-common';
import { getOwnerUsername } from '@mode/shared/util-js';
import { ToastService } from '@mode/shared/util-toast';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiKeyFetchService } from '@mode/shared/data-access-webapp';

@Injectable()
export class ApiKeyEffects {
  loadApiKeys$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApiKeyActions.loadApiKeys),
      switchMap((action) =>
        this.client.getApiKeys(action.username, action.page, action.query, action.filter || 'all').pipe(
          map((apiKeys: OrgTypes.ApiKeys) =>
            ApiKeyActions.loadApiKeysSuccess({
              pagination: apiKeys.pagination,
              apiKeys: apiKeys.apiKeys,
              apiKeyCreateForm: apiKeys.createForm,
            })
          ),
          catchError((error) => of(ApiKeyActions.loadApiKeysFailure({ error })))
        )
      )
    );
  });

  loadMemberApiKeys$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApiKeyActions.loadMemberApiKeys),
      switchMap((action) =>
        this.client.getApiKeys(action.username, action.page, action.query, 'member', action.memberUsername).pipe(
          map((apiKeys: OrgTypes.ApiKeys) =>
            ApiKeyActions.loadMemberApiKeysSuccess({
              pagination: apiKeys.pagination,
              apiKeys: apiKeys.apiKeys,
              apiKeyCreateForm: apiKeys.createForm,
            })
          ),
          catchError((error) => of(ApiKeyActions.loadMemberApiKeysFailure({ error })))
        )
      )
    );
  });

  revokeApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeyActions.revokeApiKey),
      switchMap((action) =>
        this.client.revokeApiKey(action.token).pipe(
          map(() => ApiKeyActions.revokeApiKeySuccess({ token: action.token })),
          catchError((error: Error) => of(ApiKeyActions.revokeApiKeyFailure({ error })))
        )
      )
    )
  );

  notifyApiKeyRevokeSucceeded$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ApiKeyActions.revokeApiKeySuccess),
        tap(() => this.toastService.success('Revoked API key'))
      );
    },
    { dispatch: false }
  );

  notifyApiKeyRevokeFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ApiKeyActions.revokeApiKeyFailure),
        tap(() => this.toastService.error('Could not revoke API key')),
        tap(({ error }) => this.bugsnag.notify(error))
      );
    },
    { dispatch: false }
  );

  reloadApiKeys$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ApiKeyActions.reloadApiKeys,
        ApiKeyActions.revokeApiKeySuccess,
        ApiKeyActions.revokeAllApiKeysSuccess,
        ApiKeyActions.bulkRevokeApiKeysSuccess,
        ApiKeyActions.createApiKeySuccess
      ),
      withLatestFrom(
        this.store.select(ApiKeySelectors.selectApiKeysPagination),
        this.store.select(ApiKeySelectors.selectApiKeysQuery),
        this.store.select(ApiKeySelectors.selectApiKeysFilter),
        this.store.select(ApiKeySelectors.selectApiKeysMemberUsername)
      ),
      map(([action, pagination, query, filter, memberUsername]) => {
        const { page } = pagination;
        const username = getOwnerUsername(window.document) || '';

        if (!memberUsername) {
          return ApiKeyActions.loadApiKeys({ query, page, username, filter });
        } else {
          return ApiKeyActions.loadMemberApiKeys({ query, page, username, memberUsername });
        }
      })
    )
  );

  createApiKey$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApiKeyActions.createApiKey),
      switchMap(({ workspace, member, config }) =>
        this.client.createApiKey({ workspace, member, config }).pipe(
          map((apiKey: OrgTypes.ApiKey) => {
            if (!apiKey.tokenSecret) throw new Error('API Key created but no token_secret was provided');

            return ApiKeyActions.createApiKeySuccess({
              token: apiKey.token,
              token_secret: apiKey.tokenSecret,
              name: apiKey.name,
            });
          }),
          catchError((error: Error | HttpErrorResponse) => of(ApiKeyActions.createApiKeyFailure({ error })))
        )
      )
    );
  });

  updateMemberApiKeysEnabled$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApiKeyActions.updateMemberApiKeysEnabled),
      switchMap((action) =>
        this.client.updateMemberApiKeysEnabled(action.workspaceUsername, action.enabled).pipe(
          map(({ memberApiTokensEnabled: enabled }) => ApiKeyActions.updateMemberApiKeysEnabledSuccess({ enabled })),
          catchError((error: Error | HttpErrorResponse) =>
            of(ApiKeyActions.updateMemberApiKeysEnabledFailure({ error }))
          )
        )
      )
    );
  });

  logApiKeyCreationFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ApiKeyActions.createApiKeyFailure),
        tap(({ error }) => {
          if (error instanceof HttpErrorResponse) {
            this.toastService.error(error.error.message);
          } else {
            this.toastService.error(error.message);
          }
        }),
        tap(({ error }) => this.bugsnag.notify(error))
      );
    },
    { dispatch: false }
  );

  bulkRevokeApiKeys$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeyActions.bulkRevokeApiKeys),
      switchMap(({ workspace, member, keyType: type }) =>
        this.apiKeyFetchService.revokeBulkApiKeys({
          workspace,
          member,
          type,
        })
      ),
      map(() => ApiKeyActions.bulkRevokeApiKeysSuccess()),
      catchError((error) => of(ApiKeyActions.bulkRevokeApiKeysFailure({ error })))
    )
  );

  revokeAllApiKeys$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeyActions.revokeAllApiKeys),
      switchMap((action) =>
        this.client.revokeAllApiKeys(action.workspaceUsername).pipe(
          map(() => ApiKeyActions.revokeAllApiKeysSuccess()),
          catchError((error: Error) => of(ApiKeyActions.revokeApiKeyFailure({ error })))
        )
      )
    )
  );

  notifyAllApiKeysRevokeSucceeded$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ApiKeyActions.revokeAllApiKeysSuccess),
        tap(() => this.toastService.success('All API keys revoked'))
      );
    },
    { dispatch: false }
  );

  notifyAllApiKeysRevokeFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ApiKeyActions.revokeAllApiKeysFailure),
        tap(() => this.toastService.error('Could not revoke all API keys')),
        tap(({ error }) => this.bugsnag.notify(error))
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private bugsnag: BugsnagService,
    private store: Store,
    private client: AccountClientService,
    private toastService: ToastService,
    private apiKeyFetchService: ApiKeyFetchService
  ) {}
}
